<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/audioConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
  'recordError',
  'recordPause',
  'recordResume',
  'playbackTimeChanged',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isRecordingPaused = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const recordedAudioUrl = ref(null);
const playbackSpeed = ref(1);
const isMuted = ref(false);
const elapsedTime = ref(0);
const totalDuration = ref(0);

const PLAYBACK_SPEEDS = [1, 1.5, 2];

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const formatSeconds = seconds => formatTimeProgress(seconds * 1000);

const AUDIO_EXTENSION_MAP = {
  'audio/ogg': 'ogg',
  'audio/mp3': 'mp3',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/webm': 'webm',
};

const getRecordPluginOptions = audioFormat => {
  const options = {
    scrollingWaveform: true,
    renderRecordedAudio: false,
  };
  if (
    audioFormat === 'audio/ogg' &&
    MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
  ) {
    options.mimeType = 'audio/ogg;codecs=opus';
  }
  return options;
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create(getRecordPluginOptions(props.audioRecordFormat)),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));
  wavesurfer.value.on('timeupdate', time => {
    elapsedTime.value = time;
    if (hasRecording.value) {
      emit(
        'playbackTimeChanged',
        `${formatSeconds(time)} / ${formatSeconds(totalDuration.value)}`
      );
    }
  });
  wavesurfer.value.on('ready', duration => {
    totalDuration.value = duration;
    if (hasRecording.value) {
      emit(
        'playbackTimeChanged',
        `${formatSeconds(0)} / ${formatSeconds(duration)}`
      );
    }
  });

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
    elapsedTime.value = 0;
    emit(
      'playbackTimeChanged',
      `${formatSeconds(0)} / ${formatSeconds(totalDuration.value)}`
    );
  });

  record.value.on('record-end', async blob => {
    try {
      const audioBlob = await convertAudio(blob, props.audioRecordFormat);
      // Use the converted blob's actual type, which may differ from the
      // requested format when the browser can't produce it (e.g. Safari falls
      // back to MP3 instead of OGG). This keeps the filename, content type, and
      // voice-note flag consistent with the real bytes.
      const audioType = audioBlob.type || props.audioRecordFormat;
      const ext = AUDIO_EXTENSION_MAP[audioType] || 'mp3';
      const fileName = `${getUuid()}.${ext}`;
      const file = new File([audioBlob], fileName, {
        type: audioType,
      });
      if (recordedAudioUrl.value) URL.revokeObjectURL(recordedAudioUrl.value);
      recordedAudioUrl.value = URL.createObjectURL(audioBlob);
      wavesurfer.value.load(recordedAudioUrl.value);
      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
      hasRecording.value = true;
      isRecording.value = false;
    } catch (error) {
      isRecording.value = false;
      hasRecording.value = false;
      emit('recordError', { error });
    }
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
    isRecordingPaused.value = false;
  }
};

const startRecording = () => {
  hasRecording.value = false;
  isPlaying.value = false;
  elapsedTime.value = 0;
  totalDuration.value = 0;
  record.value.startRecording();
  isRecording.value = true;
  isRecordingPaused.value = false;
};

// Re-recording (a fresh take, discarding the current one) reuses the same
// wavesurfer/mic-stream instance rather than tearing the component down -
// RecordPlugin.startRecording() is safe to call again on an already-used
// stream (see node_modules/.../wavesurfer.js/dist/plugins/record.js).
const restartRecording = () => {
  if (recordedAudioUrl.value) {
    URL.revokeObjectURL(recordedAudioUrl.value);
    recordedAudioUrl.value = null;
  }
  startRecording();
};

const pauseRecording = () => {
  if (isRecording.value && !isRecordingPaused.value) {
    record.value.pauseRecording();
    isRecordingPaused.value = true;
    emit('recordPause');
  }
};

const resumeRecording = () => {
  if (isRecording.value && isRecordingPaused.value) {
    record.value.resumeRecording();
    isRecordingPaused.value = false;
    emit('recordResume');
  }
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

const cycleSpeed = () => {
  if (!hasRecording.value) return;
  const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed.value);
  const next = PLAYBACK_SPEEDS[(currentIndex + 1) % PLAYBACK_SPEEDS.length];
  playbackSpeed.value = next;
  wavesurfer.value.setPlaybackRate(next);
};

const toggleMute = () => {
  if (!hasRecording.value) return;
  isMuted.value = !isMuted.value;
  wavesurfer.value.setMuted(isMuted.value);
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (recordedAudioUrl.value) {
    URL.revokeObjectURL(recordedAudioUrl.value);
    recordedAudioUrl.value = null;
  }
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({
  playPause,
  stopRecording,
  startRecording,
  restartRecording,
  pauseRecording,
  resumeRecording,
  cycleSpeed,
  toggleMute,
  record,
  isRecordingPaused,
  isPlaying,
  hasRecording,
  playbackSpeed,
  isMuted,
  elapsedTime,
  totalDuration,
});
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
